import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import moment from 'moment-timezone';
import { switchMap, take } from 'rxjs';
import { MessageService } from 'primeng/api';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { TargetingPreferencesService } from '../../../../../Common/TargetingShared/services/targeting-preferences.service';

import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { TargetingResultModel } from '../../../models/targeting-result.model';
import { TargetingMiscApi } from '../../../../../api/targeting/targeting-misc.api';
import { CsvExportApiModel } from '../../../../../api/targeting/model/csv-export.api.model';
import { download } from '../../../../../Common/Misc/helpers/download/download.helper';
import { SourcePickerService } from '../../../services/source-picker-service';
import { CsvExportField } from './csv-export-field.enum';
import { TargetingSearchService } from '../../../services/targeting-search.service';
import { DistributionApi } from '../../../../../api/distributions/distribution.api';
import { DistributionReportStat } from '../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { TargetingQueryApi } from '../../../../../api/targeting/model/targeting-search.api.model';
import { UserAppMessageTypeEnum, UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { CsvExportFieldsDefault } from './csv-export-dialog.models';
import { HandleTypeEnum } from '@api/targeting/enum/handle-type.enum';

@Component({
    selector: 'agpr-csv-export-dialog',
    templateUrl: './csv-export-dialog.component.html',
    styleUrls: ['./csv-export-dialog.component.scss'],
    standalone: false
})
export class CsvExportDialogComponent extends AbstractDialogComponent implements OnInit {

    readonly messageSeverities = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;

    @Input() selectedEntities: TargetingResultModel[];
    @Input() allSelected: boolean;
    @Input() id: string = null;
    @Input() hasOutlets: boolean;
    @Input() hasContacts: boolean;
    @Input() title: string = '';
    @Input() isDistributionRecipientExport: boolean = false;
    // For Distribution Recipients Export
    @Input() distributionId: number;
    @Input() distributionReportStatType: DistributionReportStat;
    @Input() distributionSelectedLink: string;
    @Input() distributionQuery: TargetingQueryApi;

    filename: string = 'Contacts_' + moment().format('YYYYMMDD');

    loading: boolean = false;
    outletFields: CsvExportFieldsDefault;
    contactFields: CsvExportFieldsDefault;
    includeLists = false;

    readonly contactProfileInfo = 'contactProfileInformationCategory';
    readonly outletProfileInfo = 'outletProfileInformationCategory';

    constructor(
        public coreDialogService: CoreDialogService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public targetingPreferencesService: TargetingPreferencesService,
        public targetingMiscApi: TargetingMiscApi,
        public sourcePickerService: SourcePickerService,
        public targetingSearchService: TargetingSearchService,
        public distributionApi: DistributionApi,
        public userPreferencesService: UserPreferencesService,
        private destroyRef: DestroyRef
    ) {
        super(coreDialogService, 'CsvExport');
    }


    // Lifecycle --

    ngOnInit() {
        // initialize checkbox fields
        this.outletFields = this.outletFieldsDefault();
        this.contactFields = this.contactFieldsDefault();

        // set up checkbox defaults
        this.userPreferencesService.getUserAppPreferences()
            .pipe(take(1))
            .subscribe(prefs => {
                const saved = prefs.contactsPrefs?.defaultSelectedCheckboxes;
                if (saved?.length) {
                    // load user saved defaults
                    this.applySelectedFields(saved);
                } else {
                    // load hardcoded defaults
                    this.setInitialSelections();
                }
            });

        // set filename
        if (this.title) {
            this.filename = `${this.title}_${moment().format('YYYYMMDD')}`;
        }
    }

    // Fields --

    private setInitialSelections() {
        this.contactFields.forEach(category => {
            category.selectedFields = category.fields.filter(field => {
                return category.initialSelection.findIndex(val => val === field.enumValue) >= 0;
            });
        });
        this.outletFields.forEach(category => {
            category.selectedFields = category.fields.filter(field => {
                return category.initialSelection.findIndex(val => val === field.enumValue) >= 0;
            });
        });
    }

    private buildApiSelectedFields() {
        const selectedFields: CsvExportField[] = [];
        this.contactFields.forEach(category => {
            category.selectedFields.forEach(x => selectedFields.push(x.enumValue));
        });
        this.outletFields.forEach(category => {
            category.selectedFields.forEach(x => selectedFields.push(x.enumValue));
        });

        // The UI simplifies the address into one field for the user,
        // but the backend will be transforming that into several colunns,
        // so we need to request them all to get the full address.
        const contactAddressIndex = selectedFields.findIndex(x => x === CsvExportField.CONTACT_ADDRESS_LINE_1);
        if (contactAddressIndex >= 0) {
            selectedFields.splice(contactAddressIndex + 1, 0,
                CsvExportField.CONTACT_ADDRESS_LINE_2,
                CsvExportField.CONTACT_CITY,
                CsvExportField.CONTACT_COUNTY,
                CsvExportField.CONTACT_PROVINCE,
                CsvExportField.CONTACT_COUNTRY,
                CsvExportField.CONTACT_POSTAL_CODE);
        }

        const outletAddressIndex = selectedFields.findIndex(x => x === CsvExportField.OUTLET_ADDRESS_LINE_1);
        if (outletAddressIndex >= 0) {
            selectedFields.splice(outletAddressIndex + 1, 0,
                CsvExportField.OUTLET_ADDRESS_LINE_2,
                CsvExportField.OUTLET_CITY,
                CsvExportField.OUTLET_COUNTY,
                CsvExportField.OUTLET_PROVINCE,
                CsvExportField.OUTLET_COUNTRY,
                CsvExportField.OUTLET_POSTAL_CODE);
        }

        if (this.includeLists) {
            selectedFields.push(CsvExportField.LISTS);
        } else {
            const index = selectedFields.indexOf(CsvExportField.LISTS);
            if (index > -1) {
                selectedFields.splice(index, 1);
            }
        }

        return selectedFields;
    }

    private applySelectedFields(savedEnumValues: string[]) {
        const applyToCategories = (categories) => {
            for (const category of categories) {
                // Find which field objects match the saved enums
                category.selectedFields = category.fields.filter(
                    field => savedEnumValues.includes(field.enumValue)
                );
                // Set the category checkbox based on whether all fields are selected
                category.checked = category.selectedFields.length === category.fields.length;
            }
        };

        if (savedEnumValues.includes(CsvExportField.LISTS)) {
            this.includeLists = true;
        } else {
            this.includeLists = false;
        }

        applyToCategories(this.contactFields);
        applyToCategories(this.outletFields);
    }


    // UI --

    public ok(): void {
        const selectedFields = this.buildApiSelectedFields();

        const requestModel: CsvExportApiModel = {
            fields: selectedFields,
        };

        if (this.selectedEntities) {
            requestModel.entityIds = this.selectedEntities.map(x => x.toEntityIdModel());
        } else if (!this.isDistributionRecipientExport) {
            if (this.allSelected) {
                requestModel.query = this.targetingSearchService.getCurrentSearchContext();
            } else {
                requestModel.query = this.sourcePickerService.getCurrentSearchContext();
            }
        } else {
            // distribution export with no entities selected
            this.allSelected = true;

        }

        this.loading = true;
        // Exporting selected distribution recipients (using ids) calls targetingMiscApi.csvExport
        // Exporting all selected distribution recipients (using query) calls distributionApi.exportAllRecipients
        if (this.isDistributionRecipientExport && this.allSelected) {
            this.distributionApi.exportAllRecipients(
                this.distributionId,
                selectedFields,
                this.distributionQuery,
                this.distributionReportStatType,
                this.distributionSelectedLink)
                .subscribe({
                    next: csvString => {
                        download.blob(download.getCSVBlob(csvString), this.filename + '.csv');

                        this.loading = false;
                        this.closeDialog(true);
                    },
                    error: err => {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            detail: 'Discover.csvExport.error',
                        });
                        this.loading = false;
                        this.closeDialog(false);
                    }
                });
        } else {
            this.targetingMiscApi.csvExport(requestModel).subscribe({
                next: csvString => {
                    download.blob(download.getCSVBlob(csvString), this.filename + '.csv');

                    this.loading = false;
                    this.closeDialog(true);
                },
                error: err => {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        detail: 'Discover.csvExport.error',
                    });
                    this.loading = false;
                    this.closeDialog(false);
                }
            });
        }
    }

    public cancel(): void {
        this.closeDialog(false);
    }

    public saveAsDefault() {
        this.userPreferencesService.getUserAppPreferences().pipe(
            take(1),
            switchMap(prefs => {
                prefs.contactsPrefs.defaultSelectedCheckboxes = this.buildApiSelectedFields();
                return this.userPreferencesService.saveChanges(prefs, UserAppMessageTypeEnum.EXPORT);
            }),
            takeUntilDestroyed(this.destroyRef)  // guards against the component destroying mid-flight
        ).subscribe();
    }

    public categoryToggled(category: any) {
        if (category.checked) {
            category.selectedFields = [...category.fields];
        } else {
            category.selectedFields = [];
            if (category.id === this.contactProfileInfo || category.id === this.outletProfileInfo) {
                category.selectedFields = [category.fields[0]];
            }
        }
    }

    public fieldToggled(category: any) {
        category.checked = category.selectedFields.length === category.fields.length;
    }


    // Defaults --

    private contactFieldsDefault(): CsvExportFieldsDefault {
        return [
            {
                id: this.contactProfileInfo,
                label: 'Discover.csvExport.fieldCategories.profileInformation',
                checked: false,
                fields: [
                    {
                        enumValue: CsvExportField.CONTACT_NAME,
                        label: 'Discover.csvExport.fields.name',
                        disabled: this.hasContacts
                    },
                    {
                        enumValue: CsvExportField.CONTACT_FIRST_NAME,
                        label: 'Discover.csvExport.fields.firstName',
                    },
                    {
                        enumValue: CsvExportField.CONTACT_LAST_NAME,
                        label: 'Discover.csvExport.fields.lastName',
                    },
                    {
                        enumValue: CsvExportField.CONTACT_JOB_TITLE,
                        label: 'Discover.csvExport.fields.jobTitle',
                    },
                    {
                        enumValue: CsvExportField.CONTACT_JOB_ROLE,
                        label: 'Discover.csvExport.fields.jobRole',
                    },
                    {
                        enumValue: CsvExportField.CONTACT_BIO,
                        label: 'Discover.csvExport.fields.editorialProfile',
                    },
                    {
                        enumValue: CsvExportField.CONTACT_SUBJECTS,
                        label: 'Discover.csvExport.fields.subjects',
                    },
                    {
                        enumValue: CsvExportField.CONTACT_LANGUAGE,
                        label: 'Discover.csvExport.fields.language',
                    }
                ],
                initialSelection: this.hasContacts ? [
                    CsvExportField.CONTACT_NAME,
                    CsvExportField.CONTACT_JOB_TITLE,
                ] : [],
                selectedFields: []
            },
            {
                label: 'Discover.csvExport.fieldCategories.contactInformation',
                checked: false,
                fields: [
                    {
                        enumValue: CsvExportField.CONTACT_EMAIL,
                        label: `Common.handleTypes.${HandleTypeEnum.EMAIL}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_PHONE,
                        label: `Common.handleTypes.${HandleTypeEnum.PHONE}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_ADDRESS_LINE_1,
                        label: 'Discover.csvExport.fields.address',
                    },
                    {
                        enumValue: CsvExportField.CONTACT_WEBSITE,
                        label: `Common.handleTypes.${HandleTypeEnum.WEBSITE}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_NOTES,
                        label: 'Discover.csvExport.fields.notes'
                    }
                ],
                initialSelection: this.hasContacts ? [
                    CsvExportField.CONTACT_EMAIL,
                    CsvExportField.CONTACT_PHONE,
                    CsvExportField.CONTACT_ADDRESS_LINE_1
                ] : [],
                selectedFields: []
            },
            {
                label: 'Discover.csvExport.fieldCategories.socialHandles',
                checked: false,
                fields: [
                    {
                        enumValue: CsvExportField.CONTACT_TWITTER,
                        label: `Common.handleTypes.${HandleTypeEnum.TWITTER}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_INSTAGRAM,
                        label: `Common.handleTypes.${HandleTypeEnum.INSTAGRAM}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_FACEBOOK,
                        label: `Common.handleTypes.${HandleTypeEnum.FACEBOOK}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_LINKEDIN,
                        label: `Common.handleTypes.${HandleTypeEnum.LINKEDIN}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_BLUESKY,
                        label: `Common.handleTypes.${HandleTypeEnum.BLUESKY}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_TIKTOK,
                        label: `Common.handleTypes.${HandleTypeEnum.TIKTOK}`,
                    },
                    {
                        enumValue: CsvExportField.CONTACT_YOUTUBE,
                        label: `Common.handleTypes.${HandleTypeEnum.YOUTUBE}`,
                    }
                ],
                initialSelection: [],
                selectedFields: []
            }
        ];
    }

    private outletFieldsDefault(): CsvExportFieldsDefault {
        return [
            {
                id: this.outletProfileInfo,
                label: 'Discover.csvExport.fieldCategories.profileInformation',
                checked: false,
                fields: [
                    {
                        enumValue: CsvExportField.OUTLET_NAME,
                        label: 'Discover.csvExport.fields.name',
                        disabled: this.hasOutlets
                    },
                    {
                        enumValue: CsvExportField.OUTLET_BIO,
                        label: 'Discover.csvExport.fields.editorialProfile',
                    },
                    {
                        enumValue: CsvExportField.OUTLET_SUBJECTS,
                        label: 'Discover.csvExport.fields.subjects',
                    },
                    {
                        enumValue: CsvExportField.OUTLET_LANGUAGE,
                        label: 'Discover.csvExport.fields.language',
                    }
                ],
                initialSelection: this.hasOutlets ? [CsvExportField.OUTLET_NAME] : [],
                selectedFields: []
            },
            {
                label: 'Discover.csvExport.fieldCategories.products',
                checked: false,
                fields: [
                    {
                        enumValue: CsvExportField.OUTLET_MEDIA_TYPE,
                        label: 'Discover.csvExport.fields.mediaType',
                    },
                    {
                        enumValue: CsvExportField.OUTLET_PRODUCT_TYPE,
                        label: 'Discover.csvExport.fields.productType',
                    },
                    {
                        enumValue: CsvExportField.OUTLET_PRODUCT_FREQUENCY,
                        label: 'Discover.csvExport.fields.frequency',
                    },
                    {
                        enumValue: CsvExportField.OUTLET_MARKET_SECTOR,
                        label: 'Discover.csvExport.fields.marketSector',
                    },
                    {
                        enumValue: CsvExportField.OUTLET_TARGET_AUDIENCE,
                        label: 'Discover.csvExport.fields.targetAudience',
                    }
                ],
                initialSelection: [],
                selectedFields: []
            },
            {
                label: 'Discover.csvExport.fieldCategories.coverage',
                checked: false,
                fields: [
                    {
                        enumValue: CsvExportField.OUTLET_AUDIENCE_REACH,
                        label: 'Discover.csvExport.fields.audienceReach',
                    },
                    {
                        enumValue: CsvExportField.OUTLET_COVERAGE_FOCUS,
                        label: 'Discover.csvExport.fields.coverageFocus',
                    },
                    {
                        enumValue: CsvExportField.OUTLET_AREA_OF_COVERAGE,
                        label: 'Discover.csvExport.fields.targetAudienceRegion',
                    }
                ],
                initialSelection: [],
                selectedFields: []
            },
            {
                label: 'Discover.csvExport.fieldCategories.contactInformation',
                checked: false,
                fields: [
                    {
                        enumValue: CsvExportField.OUTLET_EMAIL,
                        label: `Common.handleTypes.${HandleTypeEnum.EMAIL}`,
                    },
                    {
                        enumValue: CsvExportField.OUTLET_PHONE,
                        label: `Common.handleTypes.${HandleTypeEnum.PHONE}`,
                    },
                    {
                        enumValue: CsvExportField.OUTLET_ADDRESS_LINE_1,
                        label: 'Discover.csvExport.fields.address',
                    }, {
                        enumValue: CsvExportField.OUTLET_WEBSITE,
                        label: `Common.handleTypes.${HandleTypeEnum.WEBSITE}`,
                    },
                    {
                        enumValue: CsvExportField.OUTLET_NOTES,
                        label: 'Discover.csvExport.fields.notes'
                    }
                ],
                initialSelection: this.hasOutlets ? [
                    CsvExportField.OUTLET_EMAIL,
                    CsvExportField.OUTLET_PHONE,
                    CsvExportField.OUTLET_ADDRESS_LINE_1
                ] : [],
                selectedFields: []
            },
            {
                label: 'Discover.csvExport.fieldCategories.socialHandles',
                checked: false,
                fields: [
                    {
                        enumValue: CsvExportField.OUTLET_TWITTER,
                        label: `Common.handleTypes.${HandleTypeEnum.TWITTER}`,
                    },
                    {
                        enumValue: CsvExportField.OUTLET_INSTAGRAM,
                        label: `Common.handleTypes.${HandleTypeEnum.INSTAGRAM}`,
                    },
                    {
                        enumValue: CsvExportField.OUTLET_FACEBOOK,
                        label: `Common.handleTypes.${HandleTypeEnum.FACEBOOK}`,
                    },
                    {

                        enumValue: CsvExportField.OUTLET_LINKEDIN,
                        label: `Common.handleTypes.${HandleTypeEnum.LINKEDIN}`,
                    },
                    {
                        enumValue: CsvExportField.OUTLET_BLUESKY,
                        label: `Common.handleTypes.${HandleTypeEnum.BLUESKY}`,
                    },
                    {
                        enumValue: CsvExportField.OUTLET_TIKTOK,
                        label: `Common.handleTypes.${HandleTypeEnum.TIKTOK}`,
                    },
                    {
                        enumValue: CsvExportField.OUTLET_YOUTUBE,
                        label: `Common.handleTypes.${HandleTypeEnum.YOUTUBE}`,
                    }
                ],
                initialSelection: [],
                selectedFields: []
            }
        ];
    }
}


